import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import generatedSongCatalog from '../lib/generatedSongCatalog.json';
import { clamp, normalizeTag } from '../lib/audioPlayerUtils';

const AudioPlayerContext = createContext(null);

const STORAGE_KEYS = {
  settings: 'wyrdmaker.audio.settings.v1',
  favorites: 'wyrdmaker.audio.favorites.v1',
  recents: 'wyrdmaker.audio.recents.v1',
  queueState: 'wyrdmaker.audio.queue.v1',
};

const MAX_RECENTS = 30;
const DEFAULT_CROSSFADE_DURATION = 3;
const CROSSFADE_OPTIONS = [1, 2, 3, 5];
const RUNTIME_METADATA_URL = process.env.REACT_APP_MEDIA_METADATA_URL || '/assets/metadata/tonies.custom.json';

const baseCatalog = Array.isArray(generatedSongCatalog)
  ? generatedSongCatalog.map((track) => ({
      ...track,
      tags: Array.isArray(track?.tags)
        ? [...new Set(track.tags.map((tag) => normalizeTag(tag)).filter(Boolean))]
        : [],
      duration:
        typeof track?.duration === 'number' && Number.isFinite(track.duration)
          ? track.duration
          : null,
    }))
  : [];

const catalogById = new Map(baseCatalog.map((track) => [track.id, track]));
const catalogIds = baseCatalog.map((track) => track.id);

function normalizeTrackLookup(value) {
  if (!value) {
    return '';
  }

  return String(value)
    .trim()
    .toLowerCase()
    .replace(/\.(mp3|ogg|wav)$/i, '');
}

function buildMetadataIndex(entries) {
  const index = new Map();

  if (!Array.isArray(entries)) {
    return index;
  }

  for (const entry of entries) {
    const candidates = [entry?.id, entry?.title, entry?.episodes];

    for (const candidate of candidates) {
      const key = normalizeTrackLookup(candidate);
      if (key && !index.has(key)) {
        index.set(key, entry);
      }
    }
  }

  return index;
}

function mergeCatalogWithMetadata(catalog, metadataEntries) {
  const metadataIndex = buildMetadataIndex(metadataEntries);
  const matchedMetadataIds = new Set();

  const merged = catalog.map((track) => {
    const normalizedId = normalizeTrackLookup(track.id);
    const normalizedFilename = normalizeTrackLookup(track.filename);
    const metadata =
      metadataIndex.get(normalizedFilename) ||
      metadataIndex.get(normalizedId) ||
      null;

    if (metadata?.id) matchedMetadataIds.add(metadata.id);

    if (!metadata) {
      return { ...track, is_active: true, collectionIds: [] };
    }

    const tags = Array.isArray(metadata?.tags)
      ? [...new Set(metadata.tags.map((tag) => normalizeTag(tag)).filter(Boolean))]
      : track.tags;

    return {
      ...track,
      title: metadata?.episodes || metadata?.title || track.title,
      tags,
      imageUrl: metadata?.pic || metadata?.image_url || track.imageUrl || null,
      duration:
        typeof metadata?.duration === 'number' && Number.isFinite(metadata.duration)
          ? metadata.duration
          : track.duration,
      model: metadata?.model || track.model || null,
      series: metadata?.series || track.series || null,
      // Champs admin — défaut true pour rétrocompatibilité
      is_active: metadata?.is_active !== false,
      collectionIds: Array.isArray(metadata?.collections) ? metadata.collections : [],
    };
  });

  // Tracks uploadés via admin non présents dans le catalogue statique
  const uploadedTracks = (metadataEntries || [])
    .filter((entry) => {
      if (!entry.filename) return false;
      if (matchedMetadataIds.has(entry.id)) return false;
      if (entry.is_active === false) return false;
      return true;
    })
    .map((entry) => {
      const tags = Array.isArray(entry.tags)
        ? [...new Set(entry.tags.map((t) => normalizeTag(t)).filter(Boolean))]
        : [];
      return {
        id: entry.id,
        filename: entry.filename,
        src: `/assets/songs/${entry.filename}`,
        title: entry.episodes || entry.title || entry.id,
        tags,
        imageUrl: entry.pic || null,
        duration: typeof entry.duration === 'number' ? entry.duration : null,
        model: entry.model || null,
        series: entry.series || null,
        is_active: true,
        collectionIds: Array.isArray(entry.collections) ? entry.collections : [],
      };
    });

  return [...merged, ...uploadedTracks];
}

function isBrowser() {
  return typeof window !== 'undefined';
}

function readJSONStorage(key, fallback) {
  if (!isBrowser()) {
    return fallback;
  }

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return fallback;
    }

    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJSONStorage(key, value) {
  if (!isBrowser()) {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore quota errors.
  }
}

function sanitizeQueue(queue) {
  if (!Array.isArray(queue)) {
    return [...catalogIds];
  }

  const unique = [];
  for (const trackId of queue) {
    if (!catalogById.has(trackId)) {
      continue;
    }
    if (!unique.includes(trackId)) {
      unique.push(trackId);
    }
  }

  return unique.length ? unique : [...catalogIds];
}

function getDefaultDisplayMode() {
  if (!isBrowser()) {
    return 'normal';
  }

  return window.matchMedia('(max-width: 768px)').matches ? 'minimal' : 'normal';
}

function getInitialSettings() {
  const stored = readJSONStorage(STORAGE_KEYS.settings, {});

  const crossfadeDuration = CROSSFADE_OPTIONS.includes(stored?.crossfadeDuration)
    ? stored.crossfadeDuration
    : DEFAULT_CROSSFADE_DURATION;

  return {
    volume:
      typeof stored?.volume === 'number'
        ? clamp(stored.volume, 0, 1)
        : 0.8,
    muted: Boolean(stored?.muted),
    displayMode:
      stored?.displayMode === 'hidden' ||
      stored?.displayMode === 'minimal' ||
      stored?.displayMode === 'normal'
        ? stored.displayMode
        : getDefaultDisplayMode(),
    repeatMode:
      stored?.repeatMode === 'one' || stored?.repeatMode === 'all'
        ? stored.repeatMode
        : 'off',
    crossfadeEnabled:
      typeof stored?.crossfadeEnabled === 'boolean'
        ? stored.crossfadeEnabled
        : true,
    crossfadeDuration,
  };
}

function getInitialFavorites() {
  const stored = readJSONStorage(STORAGE_KEYS.favorites, []);
  if (!Array.isArray(stored)) {
    return [];
  }

  return stored.filter((trackId) => catalogById.has(trackId));
}

function getInitialRecents() {
  const stored = readJSONStorage(STORAGE_KEYS.recents, []);
  if (!Array.isArray(stored)) {
    return [];
  }

  return stored.filter((trackId) => catalogById.has(trackId)).slice(0, MAX_RECENTS);
}

function getInitialQueueState() {
  const stored = readJSONStorage(STORAGE_KEYS.queueState, {});
  const queue = sanitizeQueue(stored?.queue);
  const currentTrackId = catalogById.has(stored?.currentTrackId)
    ? stored.currentTrackId
    : queue[0] || null;

  return { queue, currentTrackId };
}

export const AudioPlayerProvider = ({ children }) => {
  const initialSettings = useMemo(() => getInitialSettings(), []);
  const initialQueueState = useMemo(() => getInitialQueueState(), []);

  const [tracks, setTracks] = useState(baseCatalog);
  const [queue, setQueue] = useState(initialQueueState.queue);
  const [currentTrackId, setCurrentTrackId] = useState(initialQueueState.currentTrackId);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(initialSettings.volume);
  const [muted, setMutedState] = useState(initialSettings.muted);
  const [displayMode, setDisplayModeState] = useState(initialSettings.displayMode);
  const [repeatMode, setRepeatModeState] = useState(initialSettings.repeatMode);
  const [crossfadeEnabled, setCrossfadeEnabledState] = useState(initialSettings.crossfadeEnabled);
  const [crossfadeDuration, setCrossfadeDurationState] = useState(initialSettings.crossfadeDuration);
  const [favorites, setFavorites] = useState(getInitialFavorites);
  const [recents, setRecents] = useState(getInitialRecents);
  const [collections, setCollections] = useState([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryTab, setLibraryTab] = useState('tags');
  const [isMobile, setIsMobile] = useState(
    () => isBrowser() && window.matchMedia('(max-width: 768px)').matches,
  );

  const queueRef = useRef(queue);
  const currentTrackIdRef = useRef(currentTrackId);
  const isPlayingRef = useRef(isPlaying);
  const volumeRef = useRef(volume);
  const mutedRef = useRef(muted);
  const repeatModeRef = useRef(repeatMode);
  const crossfadeEnabledRef = useRef(crossfadeEnabled);
  const crossfadeDurationRef = useRef(crossfadeDuration);
  const isMobileRef = useRef(isMobile);

  const audioDecksRef = useRef({ A: null, B: null });
  const activeDeckRef = useRef('A');
  const transitionRef = useRef({ rafId: null, inProgress: false });
  const playNonceRef = useRef(0);
  const crossfadeWindowUsedRef = useRef(false);

  const currentTrack = currentTrackId ? catalogById.get(currentTrackId) || null : null;

  const currentQueueIndex = useMemo(() => {
    if (!currentTrackId) {
      return -1;
    }

    return queue.indexOf(currentTrackId);
  }, [queue, currentTrackId]);

  const favoritesSet = useMemo(() => new Set(favorites), [favorites]);
  const recentsSet = useMemo(() => new Set(recents), [recents]);

  const tagCounts = useMemo(() => {
    const counter = new Map();
    for (const track of tracks) {
      if (!track.tags.length) {
        continue;
      }

      for (const tag of track.tags) {
        counter.set(tag, (counter.get(tag) || 0) + 1);
      }
    }

    return [...counter.entries()]
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => a.tag.localeCompare(b.tag));
  }, [tracks]);

  const uncategorizedTracks = useMemo(
    () => tracks.filter((track) => !track.tags.length),
    [tracks],
  );

  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  useEffect(() => {
    currentTrackIdRef.current = currentTrackId;
  }, [currentTrackId]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  useEffect(() => {
    repeatModeRef.current = repeatMode;
  }, [repeatMode]);

  useEffect(() => {
    crossfadeEnabledRef.current = crossfadeEnabled;
  }, [crossfadeEnabled]);

  useEffect(() => {
    crossfadeDurationRef.current = crossfadeDuration;
  }, [crossfadeDuration]);

  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  const getInactiveDeckName = useCallback(() => (activeDeckRef.current === 'A' ? 'B' : 'A'), []);

  const getActiveAudio = useCallback(
    () => audioDecksRef.current[activeDeckRef.current],
    [],
  );

  const getInactiveAudio = useCallback(
    () => audioDecksRef.current[getInactiveDeckName()],
    [getInactiveDeckName],
  );

  const getCurrentVolume = useCallback(() => (mutedRef.current ? 0 : volumeRef.current), []);

  const syncAudioVolumes = useCallback(() => {
    const activeAudio = getActiveAudio();
    const inactiveAudio = getInactiveAudio();
    const liveVolume = getCurrentVolume();

    if (!transitionRef.current.inProgress && activeAudio) {
      activeAudio.volume = liveVolume;
    }

    if (!transitionRef.current.inProgress && inactiveAudio) {
      inactiveAudio.volume = 0;
    }
  }, [getActiveAudio, getCurrentVolume, getInactiveAudio]);

  const stopTransition = useCallback(() => {
    if (transitionRef.current.rafId) {
      cancelAnimationFrame(transitionRef.current.rafId);
    }

    transitionRef.current.rafId = null;
    transitionRef.current.inProgress = false;
  }, []);

  const getNextTrackId = useCallback(() => {
    const liveQueue = queueRef.current;
    const liveCurrentTrackId = currentTrackIdRef.current;

    if (!liveQueue.length || !liveCurrentTrackId) {
      return liveQueue[0] || null;
    }

    const currentIndex = liveQueue.indexOf(liveCurrentTrackId);

    if (repeatModeRef.current === 'one') {
      return liveCurrentTrackId;
    }

    if (currentIndex < 0) {
      return liveQueue[0] || null;
    }

    if (currentIndex < liveQueue.length - 1) {
      return liveQueue[currentIndex + 1];
    }

    if (repeatModeRef.current === 'all') {
      return liveQueue[0] || null;
    }

    return null;
  }, []);

  const getPreviousTrackId = useCallback(() => {
    const liveQueue = queueRef.current;
    const liveCurrentTrackId = currentTrackIdRef.current;

    if (!liveQueue.length || !liveCurrentTrackId) {
      return liveQueue[0] || null;
    }

    const currentIndex = liveQueue.indexOf(liveCurrentTrackId);

    if (currentIndex > 0) {
      return liveQueue[currentIndex - 1];
    }

    if (repeatModeRef.current === 'all') {
      return liveQueue[liveQueue.length - 1] || null;
    }

    return liveCurrentTrackId;
  }, []);

  const preloadUpcomingTrack = useCallback(() => {
    if (transitionRef.current.inProgress) {
      return;
    }

    const nextTrackId = getNextTrackId();
    if (!nextTrackId || nextTrackId === currentTrackIdRef.current) {
      return;
    }

    const nextTrack = catalogById.get(nextTrackId);
    const inactiveAudio = getInactiveAudio();

    if (!nextTrack || !inactiveAudio || !inactiveAudio.paused) {
      return;
    }

    if (inactiveAudio.src.includes(nextTrack.src)) {
      return;
    }

    inactiveAudio.preload = isMobileRef.current ? 'metadata' : 'auto';
    inactiveAudio.src = nextTrack.src;
    inactiveAudio.volume = 0;
    inactiveAudio.load();
  }, [getInactiveAudio, getNextTrackId]);

  const appendRecent = useCallback((trackId) => {
    setRecents((previous) => [trackId, ...previous.filter((id) => id !== trackId)].slice(0, MAX_RECENTS));
  }, []);

  const runCrossfade = useCallback(
    (fromAudio, toAudio, fadeDurationSeconds, nonce) => {
      stopTransition();
      transitionRef.current.inProgress = true;

      const liveTargetVolume = getCurrentVolume();
      const fromStartVolume = typeof fromAudio?.volume === 'number' ? fromAudio.volume : liveTargetVolume;
      const durationMs = Math.max(120, Math.floor(fadeDurationSeconds * 1000));
      const startAt = performance.now();

      const tick = (timestamp) => {
        if (nonce !== playNonceRef.current) {
          stopTransition();
          return;
        }

        const progressRatio = clamp((timestamp - startAt) / durationMs, 0, 1);

        if (fromAudio) {
          fromAudio.volume = Math.max(0, fromStartVolume * (1 - progressRatio));
        }

        if (toAudio) {
          toAudio.volume = liveTargetVolume * progressRatio;
        }

        if (progressRatio < 1) {
          transitionRef.current.rafId = requestAnimationFrame(tick);
          return;
        }

        transitionRef.current.rafId = null;
        transitionRef.current.inProgress = false;

        if (fromAudio) {
          fromAudio.pause();
          fromAudio.currentTime = 0;
          fromAudio.volume = 0;
        }

        if (toAudio) {
          toAudio.volume = liveTargetVolume;
        }

        preloadUpcomingTrack();
      };

      transitionRef.current.rafId = requestAnimationFrame(tick);
    },
    [getCurrentVolume, preloadUpcomingTrack, stopTransition],
  );

  const playTrackById = useCallback(
    async (trackId, options = {}) => {
      if (!catalogById.has(trackId)) {
        return false;
      }

      const { forceNoFade = false, ensureInQueue = true, trackRecents = true } = options;

      let liveQueue = queueRef.current;
      if (ensureInQueue && !liveQueue.includes(trackId)) {
        liveQueue = [...liveQueue, trackId];
        setQueue(liveQueue);
      }

      if (!liveQueue.length) {
        liveQueue = [trackId];
        setQueue(liveQueue);
      }

      const track = catalogById.get(trackId);
      const previousTrackId = currentTrackIdRef.current;
      const hasDifferentCurrent = previousTrackId && previousTrackId !== trackId;
      const shouldCrossfade =
        crossfadeEnabledRef.current &&
        !forceNoFade &&
        isPlayingRef.current &&
        hasDifferentCurrent;

      const nonce = ++playNonceRef.current;
      stopTransition();
      crossfadeWindowUsedRef.current = false;

      const activeAudio = getActiveAudio();
      const inactiveDeckName = getInactiveDeckName();
      const inactiveAudio = audioDecksRef.current[inactiveDeckName];
      const liveVolume = getCurrentVolume();

      if (!activeAudio) {
        return false;
      }

      const startAudio = async (audio, src, initialVolume) => {
        audio.pause();
        audio.src = src;
        audio.preload = isMobileRef.current ? 'metadata' : 'auto';
        audio.currentTime = 0;
        audio.volume = initialVolume;

        try {
          await audio.play();
          return true;
        } catch {
          return false;
        }
      };

      if (shouldCrossfade && inactiveAudio) {
        const started = await startAudio(inactiveAudio, track.src, 0);

        if (!started || nonce !== playNonceRef.current) {
          return false;
        }

        activeDeckRef.current = inactiveDeckName;
        setCurrentTrackId(trackId);
        setProgress(0);
        setDuration(track.duration || inactiveAudio.duration || 0);
        setIsPlaying(true);

        if (trackRecents) {
          appendRecent(trackId);
        }

        runCrossfade(activeAudio, inactiveAudio, crossfadeDurationRef.current, nonce);
        return true;
      }

      const fallbackAudio = activeAudio;
      const secondaryAudio = inactiveAudio;

      if (secondaryAudio) {
        secondaryAudio.pause();
        secondaryAudio.currentTime = 0;
        secondaryAudio.volume = 0;
      }

      const started = await startAudio(fallbackAudio, track.src, liveVolume);

      if (nonce !== playNonceRef.current) {
        return false;
      }

      setCurrentTrackId(trackId);
      setProgress(0);
      setDuration(track.duration || fallbackAudio.duration || 0);
      setIsPlaying(started);

      if (started && trackRecents) {
        appendRecent(trackId);
      }

      preloadUpcomingTrack();
      return started;
    },
    [
      appendRecent,
      getActiveAudio,
      getCurrentVolume,
      getInactiveDeckName,
      preloadUpcomingTrack,
      runCrossfade,
      stopTransition,
    ],
  );

  const stopImmediate = useCallback(() => {
    playNonceRef.current += 1;
    stopTransition();

    const { A, B } = audioDecksRef.current;
    for (const audio of [A, B]) {
      if (!audio) {
        continue;
      }
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 0;
    }

    setIsPlaying(false);
    setProgress(0);
  }, [stopTransition]);

  const playNext = useCallback(
    async (options = {}) => {
      const nextTrackId = getNextTrackId();
      if (!nextTrackId) {
        stopImmediate();
        return false;
      }

      return playTrackById(nextTrackId, { forceNoFade: false, ...options });
    },
    [getNextTrackId, playTrackById, stopImmediate],
  );

  const playPrevious = useCallback(async () => {
    const activeAudio = getActiveAudio();
    if (activeAudio && activeAudio.currentTime > 3) {
      activeAudio.currentTime = 0;
      setProgress(0);
      return true;
    }

    const previousTrackId = getPreviousTrackId();
    if (!previousTrackId) {
      return false;
    }

    return playTrackById(previousTrackId, { forceNoFade: false });
  }, [getActiveAudio, getPreviousTrackId, playTrackById]);

  const playFromQueue = useCallback(
    (trackId) => playTrackById(trackId, { forceNoFade: false, ensureInQueue: true }),
    [playTrackById],
  );

  const playOrResume = useCallback(async () => {
    const activeAudio = getActiveAudio();

    if (!currentTrackIdRef.current) {
      const fallbackTrackId = queueRef.current[0] || null;
      if (!fallbackTrackId) {
        return false;
      }
      return playTrackById(fallbackTrackId, { forceNoFade: true });
    }

    if (!activeAudio) {
      return false;
    }

    try {
      await activeAudio.play();
      setIsPlaying(true);
      preloadUpcomingTrack();
      return true;
    } catch {
      return false;
    }
  }, [getActiveAudio, playTrackById, preloadUpcomingTrack]);

  const pausePlayback = useCallback(() => {
    const activeAudio = getActiveAudio();
    if (!activeAudio) {
      return;
    }

    activeAudio.pause();
    setIsPlaying(false);
  }, [getActiveAudio]);

  const togglePlayPause = useCallback(() => {
    if (isPlayingRef.current) {
      pausePlayback();
      return;
    }

    playOrResume();
  }, [pausePlayback, playOrResume]);

  const seekTo = useCallback(
    (nextTime) => {
      const activeAudio = getActiveAudio();
      if (!activeAudio) {
        return;
      }

      const safeTime = clamp(nextTime, 0, Number.isFinite(activeAudio.duration) ? activeAudio.duration : nextTime);
      activeAudio.currentTime = safeTime;
      setProgress(safeTime);
      crossfadeWindowUsedRef.current = false;
    },
    [getActiveAudio],
  );

  const addToQueueEnd = useCallback((trackId) => {
    if (!catalogById.has(trackId)) {
      return;
    }

    setQueue((previous) => {
      if (previous.includes(trackId)) {
        return previous;
      }
      return [...previous, trackId];
    });
  }, []);

  const addToQueueNext = useCallback(
    (trackId) => {
      if (!catalogById.has(trackId)) {
        return;
      }

      setQueue((previous) => {
        const withoutTrack = previous.filter((id) => id !== trackId);
        const currentIndex = withoutTrack.indexOf(currentTrackIdRef.current);

        if (currentIndex < 0) {
          return [trackId, ...withoutTrack];
        }

        const nextQueue = [...withoutTrack];
        nextQueue.splice(currentIndex + 1, 0, trackId);
        return nextQueue;
      });
    },
    [],
  );

  const removeFromQueue = useCallback((trackId) => {
    setQueue((previous) => {
      const nextQueue = previous.filter((id) => id !== trackId);
      return nextQueue.length ? nextQueue : previous;
    });
  }, []);

  const toggleFavorite = useCallback((trackId) => {
    if (!catalogById.has(trackId)) {
      return;
    }

    setFavorites((previous) => {
      if (previous.includes(trackId)) {
        return previous.filter((id) => id !== trackId);
      }
      return [trackId, ...previous];
    });
  }, []);

  const setVolume = useCallback((nextVolume) => {
    const normalizedVolume = clamp(nextVolume, 0, 1);
    setVolumeState(normalizedVolume);

    if (normalizedVolume > 0 && mutedRef.current) {
      setMutedState(false);
    }
  }, []);

  const toggleMute = useCallback(() => {
    setMutedState((previous) => !previous);
  }, []);

  const cycleRepeatMode = useCallback(() => {
    setRepeatModeState((previous) => {
      if (previous === 'off') {
        return 'all';
      }

      if (previous === 'all') {
        return 'one';
      }

      return 'off';
    });
  }, []);

  const setRepeatMode = useCallback((nextMode) => {
    if (nextMode !== 'off' && nextMode !== 'all' && nextMode !== 'one') {
      return;
    }

    setRepeatModeState(nextMode);
  }, []);

  const setDisplayMode = useCallback((nextMode) => {
    if (nextMode !== 'normal' && nextMode !== 'minimal' && nextMode !== 'hidden') {
      return;
    }

    setDisplayModeState(nextMode);
  }, []);

  const setCrossfadeEnabled = useCallback((enabled) => {
    setCrossfadeEnabledState(Boolean(enabled));
  }, []);

  const setCrossfadeDuration = useCallback((nextDuration) => {
    const numericDuration = Number(nextDuration);
    const safeDuration = CROSSFADE_OPTIONS.includes(numericDuration)
      ? numericDuration
      : DEFAULT_CROSSFADE_DURATION;

    setCrossfadeDurationState(safeDuration);
  }, []);

  const playCollectionRandom = useCallback((collectionId) => {
    const col = collections.find((c) => c.id === collectionId);
    if (!col || !col.track_ids?.length) return;
    const shuffled = [...col.track_ids].sort(() => Math.random() - 0.5);
    const validIds = shuffled.filter((id) => catalogById.has(id));
    if (!validIds.length) return;
    setQueue(validIds);
    const first = validIds[0];
    setCurrentTrackId(first);
    const deck = audioDecksRef.current[activeDeckRef.current];
    const track = catalogById.get(first);
    if (deck && track?.src) {
      deck.src = track.src;
      deck.load();
      deck.play().catch(() => {});
      setIsPlaying(true);
    }
  }, [collections]);

  const openLibrary = useCallback((nextTab = 'tags') => {
    setLibraryTab(nextTab);
    setLibraryOpen(true);
  }, []);

  const closeLibrary = useCallback(() => {
    setLibraryOpen(false);
  }, []);

  useEffect(() => {
    if (!isBrowser()) {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (event) => {
      setIsMobile(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (!isBrowser()) {
      return;
    }

    let isCancelled = false;

    const loadRuntimeMetadata = async () => {
      try {
        const response = await fetch(RUNTIME_METADATA_URL, {
          method: 'GET',
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
          return;
        }

        const payload = await response.json();
        if (isCancelled || !Array.isArray(payload)) {
          return;
        }

        const mergedTracks = mergeCatalogWithMetadata(baseCatalog, payload);
        // Filtrer les tracks désactivés par l'admin
        const activeTracks = mergedTracks.filter((t) => t.is_active !== false);
        for (const track of activeTracks) {
          catalogById.set(track.id, track);
        }
        setTracks(activeTracks);
      } catch {
        // Keep base catalog when runtime metadata is unavailable.
      }
    };

    loadRuntimeMetadata();

    return () => {
      isCancelled = true;
    };
  }, []);

  // Chargement des collections MJ (endpoint authentifié)
  useEffect(() => {
    if (!isBrowser()) return;
    const token = window.localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    const loadCollections = async () => {
      try {
        const res = await fetch('/api/audio/collections', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data?.collections)) {
          setCollections(data.collections);
        }
      } catch {
        // Silencieux : les collections sont optionnelles
      }
    };
    loadCollections();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    writeJSONStorage(STORAGE_KEYS.settings, {
      volume,
      muted,
      displayMode,
      repeatMode,
      crossfadeEnabled,
      crossfadeDuration,
    });
  }, [crossfadeDuration, crossfadeEnabled, displayMode, muted, repeatMode, volume]);

  useEffect(() => {
    writeJSONStorage(STORAGE_KEYS.favorites, favorites);
  }, [favorites]);

  useEffect(() => {
    writeJSONStorage(STORAGE_KEYS.recents, recents);
  }, [recents]);

  useEffect(() => {
    writeJSONStorage(STORAGE_KEYS.queueState, {
      queue,
      currentTrackId,
    });
  }, [queue, currentTrackId]);

  useEffect(() => {
    syncAudioVolumes();
  }, [muted, syncAudioVolumes, volume]);

  useEffect(() => {
    const createAudio = () => {
      if (typeof Audio === 'undefined') {
        return null;
      }
      return new Audio();
    };

    const audioA = createAudio();
    const audioB = createAudio();

    if (!audioA || !audioB) {
      return undefined;
    }

    audioDecksRef.current = { A: audioA, B: audioB };
    audioA.preload = 'metadata';
    audioB.preload = 'none';

    if (currentTrackIdRef.current) {
      const initialTrack = catalogById.get(currentTrackIdRef.current);
      if (initialTrack) {
        audioA.src = initialTrack.src;
        audioA.load();
      }
    }

    const handleTimeUpdate = (event) => {
      const sourceAudio = event.currentTarget;
      if (sourceAudio !== getActiveAudio()) {
        return;
      }

      const currentTime = Number.isFinite(sourceAudio.currentTime) ? sourceAudio.currentTime : 0;
      const totalDuration = Number.isFinite(sourceAudio.duration) ? sourceAudio.duration : 0;

      setProgress(currentTime);
      if (totalDuration > 0) {
        setDuration(totalDuration);
      }

      if (!isPlayingRef.current) {
        return;
      }

      const remaining = totalDuration - currentTime;
      const shouldAutoCrossfade =
        crossfadeEnabledRef.current &&
        !crossfadeWindowUsedRef.current &&
        !transitionRef.current.inProgress &&
        repeatModeRef.current !== 'one' &&
        totalDuration > 0 &&
        remaining > 0 &&
        remaining <= crossfadeDurationRef.current;

      if (shouldAutoCrossfade) {
        crossfadeWindowUsedRef.current = true;
        const nextTrackId = getNextTrackId();
        if (nextTrackId && nextTrackId !== currentTrackIdRef.current) {
          playTrackById(nextTrackId, { forceNoFade: false });
        }
      }

      if (remaining > crossfadeDurationRef.current + 0.4) {
        crossfadeWindowUsedRef.current = false;
      }
    };

    const handleLoadedMetadata = (event) => {
      const sourceAudio = event.currentTarget;
      if (sourceAudio !== getActiveAudio()) {
        return;
      }

      if (Number.isFinite(sourceAudio.duration)) {
        setDuration(sourceAudio.duration);
      }
    };

    const handleEnded = (event) => {
      const sourceAudio = event.currentTarget;
      if (sourceAudio !== getActiveAudio()) {
        return;
      }

      if (transitionRef.current.inProgress) {
        return;
      }

      crossfadeWindowUsedRef.current = false;

      const nextTrackId = getNextTrackId();
      if (nextTrackId) {
        playTrackById(nextTrackId, {
          forceNoFade: !crossfadeEnabledRef.current,
        });
        return;
      }

      setIsPlaying(false);
      setProgress(0);
    };

    const handlePlay = (event) => {
      const sourceAudio = event.currentTarget;
      if (sourceAudio !== getActiveAudio()) {
        return;
      }

      setIsPlaying(true);
    };

    const handlePause = (event) => {
      const sourceAudio = event.currentTarget;
      if (sourceAudio !== getActiveAudio()) {
        return;
      }

      if (!transitionRef.current.inProgress) {
        setIsPlaying(false);
      }
    };

    for (const audio of [audioA, audioB]) {
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
    }

    return () => {
      stopTransition();
      playNonceRef.current += 1;

      for (const audio of [audioA, audioB]) {
        audio.pause();
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('pause', handlePause);
      }
    };
  }, [getActiveAudio, getNextTrackId, playTrackById, stopTransition]);

  useEffect(() => {
    if (!isBrowser()) {
      return;
    }

    const handleKeyDown = (event) => {
      const target = event.target;
      const isTypingTarget =
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(target.tagName));

      if (isTypingTarget) {
        return;
      }

      if (event.code === 'Space') {
        event.preventDefault();
        togglePlayPause();
        return;
      }

      if (event.key === 'n' || event.key === 'N') {
        event.preventDefault();
        playNext();
        return;
      }

      if (event.key === 'm' || event.key === 'M') {
        event.preventDefault();
        toggleMute();
        return;
      }

      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        setVolume(clamp(volumeRef.current + 0.05, 0, 1));
        return;
      }

      if (event.key === '-') {
        event.preventDefault();
        setVolume(clamp(volumeRef.current - 0.05, 0, 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [playNext, setVolume, toggleMute, togglePlayPause]);

  const value = {
    tracks,
    queue,
    currentTrack,
    currentTrackId,
    currentQueueIndex,
    isPlaying,
    progress,
    duration,
    volume,
    muted,
    repeatMode,
    crossfadeEnabled,
    crossfadeDuration,
    crossfadeOptions: CROSSFADE_OPTIONS,
    displayMode,
    favorites,
    favoritesSet,
    recents,
    recentsSet,
    tagCounts,
    uncategorizedTracks,
    collections,
    libraryOpen,
    libraryTab,
    isMobile,
    setLibraryOpen,
    setLibraryTab,
    openLibrary,
    closeLibrary,
    playTrackById,
    playFromQueue,
    playNext,
    playPrevious,
    playOrResume,
    pausePlayback,
    togglePlayPause,
    seekTo,
    stopImmediate,
    addToQueueNext,
    addToQueueEnd,
    removeFromQueue,
    toggleFavorite,
    setVolume,
    toggleMute,
    cycleRepeatMode,
    setRepeatMode,
    setDisplayMode,
    setCrossfadeEnabled,
    setCrossfadeDuration,
    playCollectionRandom,
  };

  return <AudioPlayerContext.Provider value={value}>{children}</AudioPlayerContext.Provider>;
};

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);

  if (!context) {
    throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  }

  return context;
};
